import { registerPage } from '@zillow/client-profiler-interface-cs';
import { logError } from '@zg-rentals/log-error';
import { MIN_LG } from '@/src/lib/breakpoints';
import { readZgEnvironment } from '@/src/lib/zgEnv';

/**
 * What kind of page this is, declared by the route that renders it.
 *
 * Deliberately not derived from the URL: `/california` rewrites to
 * `/state/[stateResourceId]`, SRPs and HDPs share the `[resourceOrMaloneId]`
 * segment, and near-me SRPs live under their own prefix, so a path matcher
 * would encode routing trivia and drift as routes move.
 *
 * Types are grouped by what shares a rendering path, since that is what the
 * web-vitals gauges measure. `sitemap` covers the state and city hubs, which
 * are built from SitemapSectionContent and seo/state-page-data and were tuned
 * as one, plus the directory hub, which links to them and is excluded from
 * marketing tags on the same basis. `user` covers every /user screen. `cms` covers the CMS
 * landing pages under /page/, which share one route and one renderer (HP-9063). Clickstream
 * already separates all of these per screen, so a bucket per route would only fragment the gauges.
 *
 * Two renderings, one list. Client Profiler and the Marlin web-vitals gauges
 * built on it keep the `hotpads-` prefix their dashboards already query;
 * the dataLayer gets the bare name.
 */
export const PAGE_TYPES = ['homepage', 'srp', 'hdp', 'bdp', 'sitemap', 'user', 'cms'] as const;

export type PageType = (typeof PAGE_TYPES)[number];

export type ClientProfilerPageName = `hotpads-${PageType}`;

export type ClientProfilerPageContext = {
  pageName: ClientProfilerPageName | 'unknown';
  pageType?: PageType;
  platform: 'desktop' | 'mobile' | 'unknown';
};

const unknownClientProfilerPageContext: ClientProfilerPageContext = {
  pageName: 'unknown',
  platform: 'unknown',
};
let clientProfilerPageContext = unknownClientProfilerPageContext;
let landingClientProfilerPageContext = unknownClientProfilerPageContext;

export function getClientProfilerPageContext(): ClientProfilerPageContext {
  return clientProfilerPageContext;
}

/**
 * The bare page type for the route currently mounted, or undefined on routes
 * that have not adopted `ClientProfilerRegistrar` yet.
 *
 * Safe to read from an ancestor's effect: React flushes child effects first,
 * so the route has registered before `AppBootstrap` runs.
 */
export function getCurrentPageType(): PageType | undefined {
  return clientProfilerPageContext.pageType;
}

/**
 * The route that served this page load. CrUX attributes a page's whole Core Web Vitals
 * lifetime to the landing URL and does not treat a soft navigation as a new page view, so
 * this is the dimension to tag web-vitals metrics with. Unlike the live context it survives
 * a route change and its unmount cleanup. Stays `unknown` for a metric that finalises before
 * the first route registers.
 */
export function getLandingClientProfilerPageContext(): ClientProfilerPageContext {
  return landingClientProfilerPageContext;
}

/**
 * Anything at or below this is a developer's own build, not a CI one. Mirrors the
 * threshold `nodeBootstrap` applies to `BUILD_NUMBER` on the server, and the value
 * `modules/workspace-cli-tools` defaults `BUILD_NUMBER` to when nothing set it.
 */
const LOCAL_BUILD_NUMBER = 1;

/** Reported in place of a build identity that no CI run produced. */
const LOCAL_BUILD = 'local';

/** A PR pipeline's ref, `PR-<id>/<n>`, as distinct from a post-merge bare integer. */
const PR_BUILD_REF_PREFIX = 'PR-';

/**
 * An unexpanded `$BUILD_NUMBER` / `$BUILD_REF` from `.env`. `loadDotEnv` runs these through
 * `dotenv-expand`, which resolves an unset variable to `''` — but a build path that skips
 * that expansion leaves the placeholder intact, and a literal `'$BUILD_REF'` sitting in a
 * Splunk field is worse than an honest `'local'`. No real build identity starts with `$`.
 */
const UNSUBSTITUTED_PLACEHOLDER = /^\$/;

/**
 * The variable's value, or `undefined` when nothing meaningful was baked in: unset, empty, or
 * an unexpanded placeholder.
 */
function definedEnv(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return !trimmed || UNSUBSTITUTED_PLACEHOLDER.test(trimmed) ? undefined : trimmed;
}

/** The value as a deployed build number, or `undefined` at or below the local-build floor. */
function deployedBuildNumber(value: string | undefined): string | undefined {
  const buildNumber = Number(value);
  return Number.isFinite(buildNumber) && buildNumber > LOCAL_BUILD_NUMBER ? `${buildNumber}` : undefined;
}

/**
 * Which build produced this bundle, as two fields, because one cannot say it.
 *
 * `buildNumber` is the join key — the same integer as the post-merge Jenkins job number and
 * Datadog's `version:` tag — and it is the key the LCP and INP attribution log lines already
 * carry (`src/bootstrap/browser.ts`), so one Splunk field joins a beacon to its log line.
 *
 * `buildRef` says which *kind* of build it was, and it is what makes `buildNumber` safe to read.
 * On a PR pipeline `BUILD_NUMBER` is only the per-job counter, so a Sherman preview reports `2`;
 * that collides across PRs and names no post-merge build. `modules/workspace-cli-tools` derives
 * `BUILD_REF` to tell the two apart, and off a PR pipeline it is the same integer as
 * `buildNumber`.
 *
 * Both report `'local'` when no CI run produced them, rather than `NaN` or `''`, so a production
 * beacon reading `local` is a visible CI failure instead of a field nobody notices is missing.
 *
 * Unlike the other `NEXT_PUBLIC_` twins these are safe to bake in at build time. One image is
 * built per CI build and deployed everywhere, so they cannot drift the way `ZG_ENV` does — which
 * is the whole reason `zgEnv.ts` exists.
 */
function readBuildIdentity(): { buildNumber: string; buildRef: string } {
  const buildRef = definedEnv(process.env.NEXT_PUBLIC_BUILD_REF);
  const buildNumber = definedEnv(process.env.NEXT_PUBLIC_BUILD_NUMBER);

  // A PR ref names a real CI build whatever its counter is, and the first job of a PR carries
  // `1`. Report that counter verbatim: `browser.ts` applies no floor either, so this is what
  // joins a Sherman beacon to its own attribution log lines, and `buildRef` is what stops a
  // reader treating it as a post-merge build number. Applying the floor here instead would be
  // arbitrary — it would report a PR's second job and drop its first.
  if (buildRef?.startsWith(PR_BUILD_REF_PREFIX)) {
    return { buildNumber: buildNumber ?? LOCAL_BUILD, buildRef };
  }

  // Off a PR pipeline the two are the same integer, so one floor decides both. It has to be a
  // floor and not a presence check: `modules/workspace-cli-tools` does `BUILD_NUMBER ||= '1'`
  // and then `BUILD_REF ||= BUILD_NUMBER`, so a local `rjs` build would otherwise beacon `'1'`,
  // which reads as a real CI build.
  return {
    buildNumber: deployedBuildNumber(buildNumber) ?? LOCAL_BUILD,
    buildRef: deployedBuildNumber(buildRef) ?? LOCAL_BUILD,
  };
}

export function registerClientProfilerPage(pageType: PageType) {
  if (typeof window === 'undefined') return;

  const { buildNumber, buildRef } = readBuildIdentity();
  const pageName: ClientProfilerPageName = `hotpads-${pageType}`;
  const platform = window.matchMedia(MIN_LG).matches ? 'desktop' : 'mobile';
  const registration = { pageName, pageType, platform } as const;
  clientProfilerPageContext = registration;
  if (landingClientProfilerPageContext === unknownClientProfilerPageContext) {
    landingClientProfilerPageContext = registration;
  }

  try {
    registerPage({
      pageName,
      // Local dev renders an empty tag; hotpads-web reported 'localhost' there.
      environment: readZgEnvironment() || 'localhost',
      platform,
      // Which build to blame for a regression. `staticDimensions` takes arbitrary keys and
      // the SDK echoes them verbatim, so this reaches the raw beacon JSON as
      // `staticDimensions.buildNumber` and Splunk reads it with `spath`. The Prometheus keys
      // behind the Umbra and Marlin web-vitals gauges are built from `environment`,
      // `pageName` and `platform` alone, so it adds a dimension without splitting a gauge.
      //
      // `buildNumber` is the key the LCP and INP attribution log lines already carry, so one
      // Splunk field joins a beacon to its log line. Not `serviceVersion`: rumlogger-keystone
      // overwrites that to 'ALL'.
      buildNumber,
      buildRef,
    });
  } catch (e) {
    logError({
      error: e,
      errorType: 'ClientProfilerRegistrationError',
      context: { pageName },
    });
  }

  return () => {
    if (clientProfilerPageContext === registration) {
      clientProfilerPageContext = unknownClientProfilerPageContext;
    }
  };
}
